import { DesignInteractionEnum, IArticleData } from '@insights/insights-data-provider';
import {
  ComponentIds,
  Entity,
  GuestConnectionsComponent,
  RoomConnectorConstants
} from '@inter-ikea-core/core';
import {
  CoreApi,
  OuterMeasurements,
  Product,
  ProductGoodToKnow,
  ProductGoodToKnowTypeName,
  ProductType,
  ShoppingItemWithPrice
} from '@inter-ikea-storageone/range';
import {
  reportAccountLogin,
  reportAddToCart,
  reportAddToWishlist,
  reportCopyDesignCode,
  reportRenderPrintQrCode,
  reportSelectImage,
  reportSelectTab,
  reportSetPostcode,
  reportSetStoreLocation,
  reportShareViaClipboard,
  reportShareViaEmail,
  reportShareViaSms,
  reportToggleModal,
  reportVpcInteraction,
  reportSaveDesign,
  ReportSourceEnum,
  reportSaveDesignClick,
  reportShareDesignCardStateChange,
  reportFinancialServicesMoreInfoClick,
  reportFinancialServicesContinueButtonClick,
  reportSummaryLoaded,
  reportAssistanceCardLinkClick
} from 'analytics/reporters';
import { ReportCurrentPageEnum } from 'components/ProductSidebar/HomeDelivery/HomeDelivery';
import { IStorageOneTranslations } from 'contexts/serviceSettings';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import { getRange } from 'utils/queryUtils';

export const getGoodToKnowInformationForPax = (products: Product[]) => {
  const frames = products.filter(product => product.type === ProductType.PAX_FRAME);

  if (frames.length > 0) {
    const tallestFrame = frames.reduce((tallest, current) =>
      tallest.communicatedDimensions.height > current.communicatedDimensions.height
        ? tallest
        : current
    );

    return tallestFrame.goodToKnow.filter(
      gtk => gtk.typeName !== ProductGoodToKnowTypeName.SOLD_SEPARATELY
    );
  }

  return [];
};

const getGoodToKnowInformationForBesta = (
  products: Product[],
  formatMessage: ({ id }: { id: string }) => string,
  coreApi: CoreApi
): ProductGoodToKnow[] => {
  const hasSuspensionRail = products.some(
    product => product.type === ProductType.BESTA_FRAME_SUSPENSION_RAIL
  );

  const hasSoftClosingDrawerRunners = products.some(
    product => product.type === ProductType.BESTA_SOFT_CLOSING
  );

  const tvBench = products.find(product => product.type === ProductType.BESTA_TV_BENCH);

  const globaltemNosWithoutHandles = [
    '60495710',
    '10489245',
    '00487888',
    '40495711',
    '50489248',
    '80487889'
  ];
  const onlyVaestervikenFront = !products.find(p => {
    const { type, globalItemNo } = p as Product;
    return (
      type === ProductType.BESTA_DOOR_DRAWER_FRONT &&
      !globaltemNosWithoutHandles.includes(globalItemNo)
    );
  });

  const hasPushOpenRunner = products.some(product => product.type === ProductType.BESTA_PUSH_OPEN);

  const isFloorStanding = (entity: Entity) => {
    const component = entity.getComponent<GuestConnectionsComponent>(
      ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID
    );
    if (
      component?.highestPriorityConnection &&
      RoomConnectorConstants.hasFloorPlacement(component.highestPriorityConnection)
    ) {
      return true;
    }
    return false;
  };

  const allEntities = coreApi.entities.getAll();
  const isStandingOnFloor = allEntities
    .map(entity => {
      return isFloorStanding(entity);
    })
    .some(v => v);

  const goodToKnow: ProductGoodToKnow[] = [];

  if (hasSuspensionRail) {
    goodToKnow.push({
      typeName: ProductGoodToKnowTypeName.COMPL_ASSEMBLY_INFORMATION,
      text: formatMessage({ id: 'storageone.bestaSafetyInfoMinimumCeiling' })
    });
    goodToKnow.push({
      typeName: ProductGoodToKnowTypeName.COMPL_ASSEMBLY_INFORMATION,
      text: formatMessage({ id: 'storageone.bestaSafetyInfoWallMaterials' })
    });
    goodToKnow.push({
      typeName: ProductGoodToKnowTypeName.COMPL_ASSEMBLY_INFORMATION,
      text: formatMessage({ id: 'storageone.bestaSafetyInfoMaxLoad' })
    });
  }

  if (hasSoftClosingDrawerRunners && !onlyVaestervikenFront) {
    goodToKnow.push({
      typeName: ProductGoodToKnowTypeName.COMPL_ASSEMBLY_INFORMATION,
      text: formatMessage({ id: 'storageone.bestaSafetyInfoSoftClosing' })
    });
  }

  if (hasPushOpenRunner) {
    goodToKnow.push({
      typeName: ProductGoodToKnowTypeName.COMPL_ASSEMBLY_INFORMATION,
      text: formatMessage({ id: 'storageone.bestaSafetyInfoPushOpen' })
    });
  }

  if (isStandingOnFloor) {
    goodToKnow.push({
      typeName: ProductGoodToKnowTypeName.WARNING,
      text: formatMessage({ id: 'storageone.bestaSafetyInfoFloorTipWarning' })
    });
  }

  if (tvBench) {
    goodToKnow.push(...tvBench.goodToKnow);
  }

  return goodToKnow;
};

export const wardrobeTextTranslation = () => {
  const range = getRange();
  switch (range) {
    case 'pax':
    case 'platsa':
      return 'storageone.wardrobe';
    case 'eket':
      return 'storageone.checkoutCabinateCombinationTitle';
    default:
      return 'storageone.checkoutStorageCombinationTitle';
  }
};

export const getGoodToKnowInformation = (
  products: Product[],
  formatMessage: ({ id }: { id: string }) => string,
  coreApi: CoreApi
) => {
  return getRange() === 'pax'
    ? getGoodToKnowInformationForPax(products)
    : getGoodToKnowInformationForBesta(products, formatMessage, coreApi);
};

export const getStringMeasurements = (
  measurements: { width: number; height: number; depth: number },
  translations: IStorageOneTranslations,
  useMetricMeasures?: boolean
) => {
  const x = adjustMeasurementsSystem(measurements.width, translations, useMetricMeasures);
  const y = adjustMeasurementsSystem(measurements.depth, translations, useMetricMeasures);
  const z = adjustMeasurementsSystem(measurements.height, translations, useMetricMeasures);

  return `${x}x${y}x${z}`;
};

export const getMeasurements = (
  outerMeasurements: OuterMeasurements | null,
  useMetricMeasures: boolean,
  formatMessage: any,
  translations: IStorageOneTranslations
): string => {
  return outerMeasurements
    ? `
  ${formatMessage({ id: wardrobeTextTranslation() })},
  ${getStringMeasurements(outerMeasurements, translations, useMetricMeasures)}
  ${formatMessage({ id: 'kompis.measureValue' })}
`
    : '';
};

export const trackAnalytics = (
  eventData: CustomEvent,
  vpcCode: string | null,
  shoppingItems: ShoppingItemWithPrice[],
  currencyCode: string
) => {
  const {
    detail: { event, data }
  } = eventData;

  switch (event) {
    case 'addToCart':
      reportAddToCart({
        designId: vpcCode || undefined,
        items: shoppingItems.map(cartItemToInsightsItem),
        currency: currencyCode
      });
      break;
    case 'addToList':
      reportAddToWishlist({
        designId: vpcCode || undefined,
        currency: currencyCode,
        items: shoppingItems.map(cartItemToInsightsItem)
      });
      break;
    case 'tabChange':
      reportSelectTab({
        tabName: data.selectedTab
      });
      break;
    case 'shareDesign':
      reportToggleModal({
        modal: 'ShareDesignSheet',
        visible: !!data.visibleModal,
        page: ReportCurrentPageEnum.summaryPage
      });
      reportVpcInteraction({
        vpcCode: vpcCode ? vpcCode : '',
        interaction: DesignInteractionEnum.storeConfiguration
      });
      break;
    case 'modalOpen':
      // Hard coded to summary_modal since we have to data from Kompis which modal is closed
      reportToggleModal({
        modal: data.visibleModal?.modal,
        visible: !!data.visibleModal,
        page: ReportCurrentPageEnum.summaryPage
      });
      break;
    case 'modalClose':
      reportToggleModal({
        modal: data.visibleModal?.modal,
        visible: !!data.visibleModal,
        page: ReportCurrentPageEnum.summaryPage
      });
      break;
    case 'selectedImageIndexChange':
      reportSelectImage({ index: data.selectedIndex, page: ReportCurrentPageEnum.summaryPage });
      break;
    case 'storeSelectorChange':
      reportSetStoreLocation({ page: ReportCurrentPageEnum.summaryPage, storeId: data.storeId });
      break;
    case 'homeDeliveryInput':
    case 'addToCartClick':
    case 'addToListClick':
    case 'confirmationCardStateChange':
      break;
    case 'resetAddToListState':
      reportToggleModal({
        modal: 'AddToListCard',
        visible: true,
        page: ReportCurrentPageEnum.summaryPage
      });
      break;
    case 'resetAddToCartState':
      reportToggleModal({
        modal: 'ConfirmationCard',
        visible: true,
        page: ReportCurrentPageEnum.summaryPage
      });
      break;
    case 'copyDesignLinkClick':
      reportShareViaClipboard();
      break;
    case 'sendByEmailSend':
      reportShareViaEmail();
      break;
    case 'sendBySmsSend':
      reportShareViaSms();
      break;
    case 'copyDesignCodeClick':
      reportCopyDesignCode();
      break;
    //This event is fired when the user clicks the big Save button under "Add to bag"
    case 'saveDesign':
      reportToggleModal({
        modal: 'SaveToProfileSheet',
        visible: true,
        page: ReportCurrentPageEnum.summaryPage
      });
      break;
    //This event is fired when the user clicks Login button in the Save Design Sheet
    case 'saveDesignToProfileLogIn':
      reportAccountLogin({
        source: ReportSourceEnum.summaryPageSaveDesignSheet
      });
      break;
    //This event is fired when the user clicks the Save button in the sheet
    case 'saveDesignToProfile':
      reportSaveDesignClick({ source: ReportSourceEnum.summaryPageSaveDesignSheet });
      break;
    //This event is ALWAYS fired when the save action happens (in background or after clicking the save button)
    case 'designSavedToProfile':
      reportSaveDesign({
        vpcCode: vpcCode ? vpcCode : '',
        designId: data.designId ? data.designId : ''
      });
      break;
    //This event is fired when the user has open Add to cart sheet and then clicks "Save" which then logs the user in and saves in one click
    case 'logInAndSaveDesign':
      reportAccountLogin({
        source: ReportSourceEnum.summaryPageAddToCartSheet
      });
      break;
    case 'printQrCodeRendered':
      reportRenderPrintQrCode({ designCode: vpcCode ? vpcCode : '' });
      break;
    case 'shareDesignCardStateChange':
      reportShareDesignCardStateChange({
        state: data.selectedState,
        source: ReportSourceEnum.shareDesignSheet
      });
      break;
    case 'shareDesignClick':
      break;
    case 'financialServicesMoreInfoClick':
      reportFinancialServicesMoreInfoClick({ page: ReportCurrentPageEnum.summaryPage });
      break;
    case 'financialServicesContinueButtonClick':
      reportFinancialServicesContinueButtonClick({ page: ReportCurrentPageEnum.summaryPage });
      break;
    case 'summaryLoaded':
      reportSummaryLoaded();
      break;
    case 'saveZipCode':
      reportSetPostcode({ page: ReportCurrentPageEnum.summaryPage });
      break;
    case 'assistanceCardLinkClick':
      reportAssistanceCardLinkClick();
      break;
    default:
      // eslint-disable-next-line no-console
      console.warn(`Unhandled analytics event "${event}" in summary component. Add it!`, data);
  }
};

export const getDesignLink = (storeId: string | null, vpcCode: string | null) => {
  const { origin, pathname, search } = window.location;
  const searchParams = new URLSearchParams(search);
  searchParams.delete('vpcSource');
  if (!storeId) {
    searchParams.delete('storeId');
  } else {
    searchParams.set('storeId', storeId);
  }
  searchParams.delete('uiPlatform');
  return `${origin}${pathname.replace(
    '/kiosk/latest/',
    '/web/latest/'
  )}?${searchParams}#/vpc/${vpcCode}`;
};

export const getVpcFromSummaryUrl = () => {
  const vpcFromSummaryUrlRegExMatch = document.location.href.match('\\/vpc\\/(\\d|\\w{5,7})');
  return vpcFromSummaryUrlRegExMatch && vpcFromSummaryUrlRegExMatch?.length > 1
    ? vpcFromSummaryUrlRegExMatch[1]
    : null;
};

const cartItemToInsightsItem = (item: ShoppingItemWithPrice): IArticleData => ({
  id: item.id,
  price: item.price,
  qty: item.quantity
});
